import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import styled from 'styled-components'
import { ButtonFooter, Layout } from 'app/ecommerce/common'
import { colors } from 'shared/assets/styles'
import {
    CatalogMenuSize,
    CatalogMenuStep,
    CatalogStepperSuggestions
} from 'app/ecommerce/common/components/catalog'
import { purchasable } from 'shared/utils/ecommerce/utils'
import type {
    PurchasableItem,
    PromotionOrigin
} from 'shared/store/ecommerce/order'
import type { Menu as MenuType } from 'shared/store/ecommerce/catalog'
import type {
    ClickAndCollectPromotion,
    MetaShortDTO,
    Coupon
} from 'shared/types'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { H2 } from 'app/common/typography'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import { AllergensAlert } from 'app/ecommerce/common/components/modal-alert'
import { useLayoutEffect } from 'react'
import { formatPrice, menuSize } from 'shared/utils'
import type { RootState } from 'shared/store/reducers'
import ProductSheetModal from '../product-sheet-modal/ProductSheetModal'
import { SPACING } from 'app-v2/design-tokens'
import { Button, CaptionHintText } from 'app-v2/design-systems'
import { Info } from 'app/assets/icons'

const { tertiary } = colors
const { XL: MENU_SIZE_XL } = menuSize

type Props = {
    currentOrder?: PurchasableItem,
    currentMenu?: MenuType,
    price: number,
    currentSuggestions: Array<PurchasableItem>,
    isImpersonalOperation?: boolean,
    orderInit?: PurchasableItem,
    isFromCart: boolean,
    promotion?: ClickAndCollectPromotion,
    promotionOrigin?: PromotionOrigin,
    custoAvailable?: boolean,
    extrasAvailable?: boolean,
    setCurrentItem: (product: ?PurchasableItem) => void,
    updateOrder: (position: number, newOrder: ?PurchasableItem) => void,
    validateOrder: (
        isFromCart: boolean,
        orderInit: ?PurchasableItem,
        promotionId: ?string
    ) => void,
    setCurrentSuggestions: (suggestions: Array<PurchasableItem>) => void,
    intl: IntlShape,
    fetchAvailableFeatures: () => void,
    showAllergensAlert: boolean,
    cartProducts: Array<PurchasableItem>,
    pageMetadata: (payload: MetaShortDTO) => void,
    sendMeta: EventMetadata => void,
    sendECommerceMeta: ECommerceMetadata => void,
    navigate: (to: string, state?: any) => void,
    collectMode: string,
    promotionToApply?: ClickAndCollectPromotion,
    featurePaidSideDishes: boolean,
    coupon: Coupon
}

type State = {
    isSizeSelected: boolean,
    showAllergensAlert: boolean
}

const Menu = ({
    currentOrder,
    currentMenu,
    price,
    currentSuggestions,
    isImpersonalOperation,
    orderInit,
    isFromCart,
    promotion,
    promotionOrigin,
    custoAvailable,
    extrasAvailable,
    setCurrentItem,
    updateOrder,
    validateOrder,
    setCurrentSuggestions,
    intl,
    fetchAvailableFeatures,
    showAllergensAlert,
    cartProducts,
    pageMetadata,
    sendMeta,
    sendECommerceMeta,
    navigate,
    collectMode,
    featurePaidSideDishes,
    promotionToApply,
    coupon
}: Props) => {
    const [isSizeSelected, setIsSizeSelected] = useState(undefined)
    const [allergensAlert, setAllergensAlert] = useState(showAllergensAlert)
    const [displayNextScreenHandler, setDisplayNextScreenHandler] = useState(
        false
    )
    const [displayProductSheet, setDisplayProductSheet] = useState(false)
    const displayPrice =
        currentOrder?.promotions && price === 0 ? undefined : price

    const selectedAllergens = useSelector(
        (state: RootState) => state.ecommerceCatalogState.selectedAllergens
    )

    const width = window.innerWidth

    const handleIsSelected = useCallback(() => {
        const isSizeSelected =
            (currentMenu &&
                !currentMenu.existsInL &&
                !currentMenu.existsInXL) ||
            promotionToApply
                ? true
                : Boolean(orderInit)

        setIsSizeSelected(isSizeSelected)
    }, [currentMenu, orderInit])

    const handleCurrentItem = useCallback(() => {
        let purchasableMenu = purchasable(
            currentMenu,
            undefined,
            promotion,
            promotionOrigin
        )
        if (isImpersonalOperation && purchasableMenu) {
            purchasableMenu = {
                ...purchasableMenu,
                isImpersonalOperation: true
            }
        }
        setCurrentItem(purchasableMenu)

        handleIsSelected()

        fetchAvailableFeatures()
        if (currentMenu) {
            sendECommerceMeta({
                type: 'ecomm_product_screen',
                data: {
                    list: currentMenu.categoryName,
                    name: currentMenu.name,
                    id: currentMenu.id,
                    price: currentMenu.price,
                    category: currentMenu.categoryName
                }
            })
            pageMetadata({ title: currentMenu.name })
        }
    }, [])

    useLayoutEffect(() => {
        if (!isFromCart) {
            handleCurrentItem()
        }
    }, [])

    useEffect(() => {
        if (currentMenu && currentMenu?.id !== currentOrder?.id) {
            handleCurrentItem()
        }
    }, [currentMenu, currentOrder])

    useEffect(() => {
        if (
            (isFromCart && !isSizeSelected) ||
            (promotion !== undefined && !isSizeSelected) ||
            (promotionToApply !== undefined && !isSizeSelected)
        ) {
            handleIsSelected()
        }
    }, [handleIsSelected, isFromCart, isSizeSelected])

    const onClickAllergensPopup = (shouldRedirect: boolean) => {
        setAllergensAlert(false)
        if (shouldRedirect) {
            navigate(
                intl.formatMessage({ id: 'routes.clickandcollect.catalog' })
            )
        }
    }

    const handleValidateOrder = (isFromCart, orderInit, promotionToApply) => {
        validateOrder(isFromCart, orderInit, promotionToApply?.promotionId)
        if (promotionToApply) {
            setDisplayNextScreenHandler(true)
        }
    }

    const cartItem = cartProducts.find(
        product => currentOrder && product.id === currentOrder.id
    )

    if (cartItem && cartItem.maxQuantity === cartItem.quantity && !isFromCart) {
        navigate(
            intl.formatMessage({
                id: 'routes.clickandcollect.catalog'
            })
        )
    }

    if (!currentMenu) {
        return null
    }

    const isDisabled = useMemo(
        () =>
            (!isSizeSelected && !promotion) ||
            !currentOrder ||
            (currentOrder &&
                currentOrder.steps &&
                currentOrder.steps.some(step => _.isEmpty(step))),
        [currentOrder, isSizeSelected]
    )

    return (
        <>
            <Layout background="plain" border backEnabled>
                {allergensAlert && (
                    <AllergensAlert
                        onClick={onClickAllergensPopup}
                        onClose={() => setAllergensAlert(false)}
                        showAllergensAlert={allergensAlert}
                    />
                )}
                <Container>
                    <StyledResponsiveContainer>
                        <GroupContainer>
                            <Title center>{currentMenu.name}</Title>

                            <StyledButtonContainer>
                                <StyledButton
                                    variant="text"
                                    small
                                    onClick={() => setDisplayProductSheet(true)}
                                    color={colors.text}>
                                    <>
                                        <Info color={colors.text} size={16} />
                                        <CaptionHintText>
                                            {intl.formatMessage({
                                                id:
                                                    'clickandcollect.catalog.product.infos'
                                            })}
                                        </CaptionHintText>
                                    </>
                                </StyledButton>
                            </StyledButtonContainer>

                            {currentMenu &&
                                currentMenu.steps &&
                                currentMenu.steps.length > 0 && (
                                    <CatalogMenuStep
                                        step={currentMenu.steps[0]}
                                        index={0}
                                        length={
                                            currentMenu && currentMenu.steps
                                                ? currentMenu.steps.length
                                                : 0
                                        }
                                        currentOrder={currentOrder}
                                        collectMode={collectMode}
                                        isFromCart={isFromCart}
                                        updateOrder={updateOrder}
                                        featureCustoAvailable={!!custoAvailable}
                                        featurePaidSideDishes={
                                            featurePaidSideDishes
                                        }
                                        featureExtraAvailable={
                                            !!extrasAvailable
                                        }
                                        isPromotion={
                                            currentMenu &&
                                            currentMenu.customizable
                                                ? false
                                                : Boolean(promotion)
                                        }
                                        hasSuggestions={
                                            isFromCart || promotion
                                                ? false
                                                : Boolean(
                                                      currentMenu &&
                                                          currentMenu.suggestions &&
                                                          currentMenu
                                                              .suggestions
                                                              .length > 0
                                                  )
                                        }
                                        sendMeta={sendMeta}
                                        selectedAllergens={selectedAllergens}
                                    />
                                )}
                            {(currentMenu.existsInL || currentMenu.existsInS) &&
                                !promotion &&
                                !promotionToApply && (
                                    <CatalogMenuSize
                                        menu={currentMenu}
                                        dispatchOrder={order =>
                                            setCurrentItem(order)
                                        }
                                        order={currentOrder}
                                        isFromCart={isFromCart}
                                        changeIsSizeSelected={(
                                            isSizeSelected: boolean
                                        ) => setIsSizeSelected(isSizeSelected)}
                                    />
                                )}
                            {currentMenu &&
                                currentMenu.steps &&
                                currentMenu.steps.length > 0 &&
                                currentMenu.steps.map(
                                    (step, index) =>
                                        (isSizeSelected || !!promotion) &&
                                        index > 0 && (
                                            <CatalogMenuStep
                                                selectedAllergens={
                                                    selectedAllergens
                                                }
                                                key={index}
                                                step={step}
                                                index={index}
                                                length={
                                                    currentMenu &&
                                                    currentMenu.steps
                                                        ? currentMenu.steps
                                                              .length
                                                        : 0
                                                }
                                                currentOrder={currentOrder}
                                                collectMode={collectMode}
                                                isFromCart={isFromCart}
                                                updateOrder={updateOrder}
                                                featureCustoAvailable={
                                                    !!custoAvailable
                                                }
                                                featurePaidSideDishes={
                                                    featurePaidSideDishes
                                                }
                                                featureExtraAvailable={
                                                    !!extrasAvailable
                                                }
                                                isPromotion={
                                                    currentMenu &&
                                                    currentMenu.customizable
                                                        ? false
                                                        : Boolean(promotion)
                                                }
                                                hasSuggestions={
                                                    isFromCart || promotion
                                                        ? false
                                                        : Boolean(
                                                              currentMenu &&
                                                                  currentMenu.suggestions &&
                                                                  currentMenu
                                                                      .suggestions
                                                                      .length >
                                                                      0
                                                          )
                                                }
                                                sendMeta={sendMeta}
                                                noExtraPriceForProducts={
                                                    currentOrder?.menuSize ===
                                                    MENU_SIZE_XL
                                                }
                                            />
                                        )
                                )}
                            {!isFromCart && !promotion && isSizeSelected && (
                                <CatalogStepperSuggestions
                                    currentItem={currentMenu}
                                    currentSuggestions={currentSuggestions}
                                    setCurrentSuggestions={
                                        setCurrentSuggestions
                                    }
                                    cartProducts={cartProducts}
                                />
                            )}
                        </GroupContainer>
                        <ButtonFooter
                            labelKey={
                                isFromCart
                                    ? 'clickandcollect.product.footer.personalize'
                                    : displayPrice
                                    ? 'clickandcollect.product.footer.price'
                                    : 'clickandcollect.product.footer'
                            }
                            labelParams={{ price: formatPrice(price) }}
                            actionUrl={intl.formatMessage({
                                id: isFromCart
                                    ? 'routes.clickandcollect.cart.summary'
                                    : 'routes.clickandcollect.catalog'
                            })}
                            params={{ coupon }}
                            onClick={() =>
                                handleValidateOrder(
                                    isFromCart,
                                    orderInit,
                                    promotionToApply
                                )
                            }
                            disabled={isDisabled}
                            shouldStick
                        />
                    </StyledResponsiveContainer>
                </Container>
                {displayProductSheet && (
                    <ProductSheetModal
                        isOpen={displayProductSheet}
                        handleClose={() => setDisplayProductSheet(false)}
                        routeId={currentMenu.routeId}
                        width={width}
                        type={currentMenu.type}
                    />
                )}
            </Layout>
        </>
    )
}

const Container = styled.div`
    overflow: auto;
    background-color: ${tertiary};
    min-height: calc(100vh - 55px);
    margin-top: 55px;

    ${media.medium`
        min-height: calc(100vh - 90px);
        margin-top:90px;
    `};
`

const GroupContainer = styled.div`
    padding-bottom: 100px;
`

const Title = styled(H2)`
    margin: ${SPACING.M} 0;
    ${media.medium`
        margin: ${SPACING.M} 0;
    `};
`

const StyledResponsiveContainer = styled(ResponsiveContainer)``

const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: center;
`

const StyledButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${SPACING.XXXS};
    text-align: center;
`

const MenuComponent = injectIntl(Menu)
export { MenuComponent }
